import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { EventTypes } from "types";

export const ChallengeVoting: React.FC = () => {
  const { gameState, currentPlayer, sendEvent } = useGame();
  const [isVoting, setIsVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  const challenge = gameState?.activeChallenge;
  const isChallenger = currentPlayer?.name === challenge?.challengerId;

  // Calculate vote counts
  const voteStats = useMemo(() => {
    if (!challenge?.votes) {
      return { overturn: 0, uphold: 0, total: 0 };
    }
    const votes = Object.values(challenge.votes);
    return {
      overturn: votes.filter((v) => v === true).length,
      uphold: votes.filter((v) => v === false).length,
      total: votes.length,
    };
  }, [challenge?.votes]);

  // Check if current player has already voted
  const alreadyVoted = useMemo(() => {
    if (!challenge?.votes || !currentPlayer) return false;
    return currentPlayer.name in challenge.votes;
  }, [challenge?.votes, currentPlayer]);

  // Get total eligible voters (all players except challenger)
  const eligibleVoters = useMemo(() => {
    if (!gameState?.players || !challenge) return 0;
    return gameState.players.filter((p) => p.name !== challenge.challengerId).length;
  }, [gameState?.players, challenge]);

  const handleVote = async (vote: boolean) => {
    if (!currentPlayer || isVoting || hasVoted || alreadyVoted) return;

    setIsVoting(true);
    try {
      await sendEvent({
        type: EventTypes.VOTE_CHALLENGE,
        voterName: currentPlayer.name,
        vote,
      });
      setHasVoted(true);
    } catch (error) {
      console.error("Error voting on challenge:", error);
    } finally {
      setIsVoting(false);
    }
  };

  if (!challenge) {
    return (
      <Center height="100%" p={4}>
        <Text textStyle="clue" fontSize="xl">
          Loading challenge...
        </Text>
      </Center>
    );
  }

  return (
    <Center height="100%" width="100%" p={4}>
      <VStack spacing={6} width="90%" maxWidth="500px">
        {/* Header */}
        <Box textAlign="center">
          <Text textStyle="clue" fontSize="2xl" color="orange.300">
            Challenge in Progress
          </Text>
          <Text textStyle="clue" fontSize="md" mt={1}>
            {challenge.challengerId} is challenging the ruling
          </Text>
        </Box>

        {/* Challenge Details */}
        <Box
          width="100%"
          bg="rgba(0,0,0,0.4)"
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor="orange.400"
        >
          <VStack spacing={3} align="stretch">
            {/* Category and Question */}
            <Box>
              <Text textStyle="clue" fontSize="sm" color="gray.400">
                Category
              </Text>
              <Text textStyle="clue" fontSize="md">
                {challenge.answerEvaluation.categoryName}
              </Text>
            </Box>

            <Divider borderColor="whiteAlpha.300" />

            <Box>
              <Text textStyle="clue" fontSize="sm" color="gray.400">
                Clue
              </Text>
              <Text textStyle="clue" fontSize="md">
                {challenge.answerEvaluation.question}
              </Text>
            </Box>

            <Divider borderColor="whiteAlpha.300" />

            {/* Answer Comparison */}
            <HStack spacing={4} justify="space-between">
              <Box flex={1}>
                <Text textStyle="clue" fontSize="sm" color="red.300">
                  Player's Answer
                </Text>
                <Text textStyle="clue" fontSize="md" fontWeight="bold">
                  "{challenge.answerEvaluation.playerAnswer || "(no answer)"}"
                </Text>
              </Box>
              <Box flex={1}>
                <Text textStyle="clue" fontSize="sm" color="green.300">
                  Correct Answer
                </Text>
                <Text textStyle="clue" fontSize="md" fontWeight="bold">
                  "{challenge.answerEvaluation.correctAnswer}"
                </Text>
              </Box>
            </HStack>

            <Divider borderColor="whiteAlpha.300" />

            {/* Points at stake */}
            <Box textAlign="center">
              <Text textStyle="clue" fontSize="sm" color="gray.400">
                Points at Stake
              </Text>
              <Text textStyle="clue" fontSize="xl" color="jeopardy.gold">
                ${challenge.clueValue.toLocaleString()}
              </Text>
            </Box>
          </VStack>
        </Box>

        {/* Vote Tally */}
        <Box
          width="100%"
          bg="whiteAlpha.100"
          p={3}
          borderRadius="md"
          textAlign="center"
        >
          <Text textStyle="clue" fontSize="sm" mb={2}>
            Current Votes ({voteStats.total}/{eligibleVoters})
          </Text>
          <HStack justify="center" spacing={6}>
            <VStack spacing={0}>
              <Text textStyle="clue" fontSize="2xl" color="green.300">
                {voteStats.overturn}
              </Text>
              <Text textStyle="clue" fontSize="xs">
                Overturn
              </Text>
            </VStack>
            <Text textStyle="clue" fontSize="xl">
              -
            </Text>
            <VStack spacing={0}>
              <Text textStyle="clue" fontSize="2xl" color="red.300">
                {voteStats.uphold}
              </Text>
              <Text textStyle="clue" fontSize="xs">
                Uphold
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* Voting Buttons or Status */}
        {isChallenger ? (
          <Box textAlign="center">
            <Text textStyle="clue" fontSize="lg" color="yellow.300">
              Waiting for other players to vote...
            </Text>
            <Text textStyle="clue" fontSize="sm" color="gray.400" mt={1}>
              You cannot vote on your own challenge
            </Text>
          </Box>
        ) : hasVoted || alreadyVoted ? (
          <Box textAlign="center">
            <Badge colorScheme="green" fontSize="md" px={4} py={2}>
              Vote Submitted
            </Badge>
            <Text textStyle="clue" fontSize="sm" color="gray.400" mt={2}>
              Waiting for other players...
            </Text>
          </Box>
        ) : (
          <Flex gap={4} width="100%">
            <Button
              flex={1}
              colorScheme="green"
              size="lg"
              onClick={() => handleVote(true)}
              isLoading={isVoting}
              isDisabled={isVoting}
            >
              <VStack spacing={0}>
                <Text fontWeight="bold">Overturn</Text>
                <Text fontSize="xs">(Award Points)</Text>
              </VStack>
            </Button>
            <Button
              flex={1}
              colorScheme="red"
              size="lg"
              onClick={() => handleVote(false)}
              isLoading={isVoting}
              isDisabled={isVoting}
            >
              <VStack spacing={0}>
                <Text fontWeight="bold">Uphold</Text>
                <Text fontSize="xs">(Ruling Stands)</Text>
              </VStack>
            </Button>
          </Flex>
        )}
      </VStack>
    </Center>
  );
};
